import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import communicationService from '../services/communicationService';
import smsService from '../services/smsService';
import AnnouncementSMSModal from '../components/communication/AnnouncementSMSModal';
import { Plus, X, Phone, CircleNotch, Lightbulb } from '@phosphor-icons/react';

function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    announcement_type: 'general',
    audience: 'all',
    is_published: true,
    publish_date: new Date().toISOString()
  });

  const loadAnnouncements = () => {
    setLoading(true);
    communicationService.getAnnouncements()
      .then(data => setAnnouncements(data.items || []))
      .catch(err => toast.error('Failed to load announcements'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await communicationService.createAnnouncement(formData);
      toast.success('Announcement created!');
      setShowModal(false);
      setFormData({
        title: '',
        content: '',
        announcement_type: 'general',
        audience: 'all',
        is_published: true,
        publish_date: new Date().toISOString()
      });
      loadAnnouncements();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenSMSModal = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowSMSModal(true);
    setSmsLoading(true);
    
    try {
      const response = await smsService.getRecipients(announcement.id);
      setPhoneNumbers(response.phone_numbers || []);
    } catch (err) {
      toast.error('Failed to load recipient list: ' + err.message);
      setPhoneNumbers([]);
    } finally {
      setSmsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="page" data-testid="announcements-page">
        <div className="page-header">
          <div>
            <h1>Announcements</h1>
            <p>School-wide communications</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="create-announcement-btn">
            <Plus size={18} weight="bold" /> New Announcement
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading announcements...</div>
        ) : (
          <div className="announcements-list">
            {announcements.map(announcement => (
              <div key={announcement.id} className="announcement-card" data-testid={`announcement-${announcement.id}`}>
                <div className="announcement-header">
                  <h3>{announcement.title}</h3>
                  <span className={`badge ${announcement.announcement_type}`}>{announcement.announcement_type}</span>
                </div>
                <p className="announcement-content">{announcement.content}</p>
                <div className="announcement-footer">
                  <span>Audience: {announcement.audience}</span>
                  <span>By: {announcement.created_by}</span>
                  <span>{new Date(announcement.publish_date).toLocaleDateString()}</span>
                </div>
                <div className="announcement-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleOpenSMSModal(announcement)}
                  >
                    <Phone size={14} /> Send SMS
                  </button>
                </div>
              </div>
            ))}
            {announcements.length === 0 && (
              <div className="empty-state">
                <p>No announcements yet</p>
              </div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="announcement-modal">
              <div className="modal-header">
                <h2>Create Announcement</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="modal-form">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    disabled={submitting}
                    data-testid="announcement-title"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={formData.announcement_type}
                      onChange={(e) => setFormData({...formData, announcement_type: e.target.value})}
                      disabled={submitting}
                    >
                      <option value="general">General</option>
                      <option value="academic">Academic</option>
                      <option value="event">Event</option>
                      <option value="emergency">Emergency</option>
                      <option value="fee">Fee Related</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Audience</label>
                    <select
                      value={formData.audience}
                      onChange={(e) => setFormData({...formData, audience: e.target.value})}
                      disabled={submitting}
                    >
                      <option value="all">All</option>
                      <option value="teachers">Teachers Only</option>
                      <option value="students">Students Only</option>
                      <option value="parents">Parents Only</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    rows={5}
                    required
                    disabled={submitting}
                    data-testid="announcement-content"
                  />
                  <p className="helper-text"><Lightbulb size={14} /> Keep messages concise if you plan to send via SMS - shorter messages deliver better and avoid provider character limits</p>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={submitting}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="publish-announcement-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Publishing...
                      </span>
                    ) : 'Publish'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Announcement SMS Modal */}
      <AnnouncementSMSModal
        isOpen={showSMSModal}
        onClose={() => {
          setShowSMSModal(false);
          setPhoneNumbers([]);
        }}
        announcement={selectedAnnouncement}
        phoneNumbers={phoneNumbers}
        loading={smsLoading}
        onSuccess={() => {
          // Optional: refresh announcements
        }}
      />
    </Layout>
  );
}

export default AnnouncementsPage;
